// components/crm/dialogs/CreateTaskDialog.jsx
import { CalendarDaysIcon, ClipboardDocumentListIcon, MagnifyingGlassIcon, PlusIcon, UserGroupIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogTrigger } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';

const EMPTY_TASK = {
  title: '',
  description: '',
  leadId: '',
  taskType: 'call',
  dueDate: '',
  priority: 'medium',
  assignedTo: '',
};

/** Thin horizontal section divider with centred label */
const SectionLabel = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 pt-1">
    <div className="h-px flex-1 bg-gray-100" />
    <span className="flex items-center gap-1 text-[10px] font-semibold text-blue-600 uppercase tracking-widest px-2">
      {Icon && <Icon className="h-3 w-3" />}
      {label}
    </span>
    <div className="h-px flex-1 bg-gray-100" />
  </div>
);

const TASK_TYPE_ICONS = {
  call:      '📞',
  demo:      '🖥️',
  follow_up: '🔁',
  meeting:   '🤝',
  email:     '✉️',
  other:     '✦',
};

export default function CreateTaskDialog({ open, onOpenChange, leads, users, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [newTask, setNewTask] = useState(EMPTY_TASK);
  const [leadSearch, setLeadSearch] = useState('');
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);

  const set  = key => val => setNewTask(f => ({ ...f, [key]: val }));
  const setE = key => e  => setNewTask(f => ({ ...f, [key]: e.target.value }));

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/tasks', newTask);
      toast.success('Task created successfully!');
      setNewTask(EMPTY_TASK);
      onSuccess();
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error(getApiErrorMessage(error, 'Failed to create task'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600 text-white">
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Task
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-[95vw] sm:max-w-[520px] md:max-w-[580px] max-h-[92vh] p-0 gap-0 flex flex-col overflow-hidden">

        {/* ── Gradient Header ──────────────────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-blue-600 to-violet-600 px-6 py-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <ClipboardDocumentListIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-white leading-tight">Create New Task</h2>
              <p className="text-xs text-blue-200 mt-0.5">Set up a follow-up task for a lead</p>
            </div>
          </div>
        </div>

        {/* ── Scrollable Body ───────────────────────────────────────────────────── */}
        <form onSubmit={handleCreateTask} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

            {/* Task Info */}
            <SectionLabel icon={ClipboardDocumentListIcon} label="Task Info" />
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Title <span className="text-red-500">*</span></Label>
                <Input
                  className="h-9 text-sm"
                  placeholder="e.g. Follow-up call with Ravi Kumar"
                  value={newTask.title}
                  onChange={setE('title')}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Description</Label>
                <Textarea
                  className="text-sm min-h-[75px] resize-none"
                  placeholder="What needs to be done in this task…"
                  value={newTask.description}
                  onChange={setE('description')}
                />
              </div>
            </div>

            {/* Task Type — visual pill selector */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Task Type</Label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(TASK_TYPE_ICONS).map(([type, icon]) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => set('taskType')(type)}
                    className={`flex items-center gap-2 border-2 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                      newTask.taskType === type
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50/50'
                    }`}
                  >
                    <span>{icon}</span>
                    <span className="capitalize">{type.replace('_', ' ')}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Schedule & Priority */}
            <SectionLabel icon={CalendarDaysIcon} label="Schedule & Priority" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Due Date <span className="text-red-500">*</span></Label>
                <Input
                  className="h-9 text-sm"
                  type="datetime-local"
                  value={newTask.dueDate}
                  onChange={setE('dueDate')}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Priority</Label>
                <Select value={newTask.priority} onValueChange={set('priority')}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high"   className="text-sm">🔴 High</SelectItem>
                    <SelectItem value="medium" className="text-sm">🟡 Medium</SelectItem>
                    <SelectItem value="low"    className="text-sm">🟢 Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Assignment */}
            <SectionLabel icon={UserGroupIcon} label="Assignment" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Searchable lead picker */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Related Lead</Label>
                <div className="relative">
                  {newTask.leadId ? (
                    <div className="flex items-center gap-2 h-9 px-3 border border-gray-200 rounded-md bg-blue-50 text-sm">
                      <span className="flex-1 truncate text-blue-800 font-medium">
                        {leads.find(l => l._id === newTask.leadId)?.name || 'Selected'}
                      </span>
                      <button type="button" onClick={() => { set('leadId')(''); setLeadSearch(''); }}
                        className="text-blue-400 hover:text-blue-700 flex-shrink-0">
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                      <input
                        className="h-9 w-full rounded-md border border-gray-200 pl-8 pr-3 text-sm outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                        placeholder="Search lead by name or phone…"
                        value={leadSearch}
                        onChange={e => { setLeadSearch(e.target.value); setShowLeadDropdown(true); }}
                        onFocus={() => setShowLeadDropdown(true)}
                        onBlur={() => setTimeout(() => setShowLeadDropdown(false), 150)}
                      />
                    </div>
                  )}
                  {showLeadDropdown && !newTask.leadId && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-44 overflow-y-auto popup-scroll">
                      {leads
                        .filter(l => {
                          const q = leadSearch.toLowerCase();
                          return !q || l.name?.toLowerCase().includes(q) || l.phone?.includes(q) || l.email?.toLowerCase().includes(q);
                        })
                        .slice(0, 30)
                        .map(lead => (
                          <button
                            key={lead._id}
                            type="button"
                            onMouseDown={() => { set('leadId')(lead._id); setLeadSearch(''); setShowLeadDropdown(false); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-50 last:border-0"
                          >
                            <p className="font-medium text-gray-800 truncate">{lead.name}</p>
                            <p className="text-xs text-gray-400 truncate">{lead.phone || lead.email}</p>
                          </button>
                        ))}
                      {leads.filter(l => {
                        const q = leadSearch.toLowerCase();
                        return !q || l.name?.toLowerCase().includes(q) || l.phone?.includes(q) || l.email?.toLowerCase().includes(q);
                      }).length === 0 && (
                        <p className="text-xs text-gray-400 text-center py-4">No leads found</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Assign To</Label>
                <Select value={newTask.assignedTo} onValueChange={set('assignedTo')}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select staff member" /></SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user._id} value={user._id} className="text-sm">
                        {user.name} — {user.role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

          </div>

          {/* ── Sticky Footer ─────────────────────────────────────────────────── */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/80 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-sm"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={loading}
              className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white text-sm px-6 gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Creating…
                </>
              ) : (
                <>
                  <ClipboardDocumentListIcon className="h-3.5 w-3.5" />
                  Create Task
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
